import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, Button } from "Components/Core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  getAssetReviewRequestById,
  patchAssetReviewRequestPaidStatus,
} from "Api/Pages/AssetReviewRequestsApi";
import {
  reassignReviewRequest,
  getKpointHighlights,
} from "Api/Pages/AssetReviewRequestsApi";
import ReassignCoach from "Components/Shared/ReassignCoach";
import { useMutation } from "react-query";
import {
  AssetReviewRequest,
  GetAssetReviewRequestsParams,
} from "Interfaces/Pages/AssetReviewRequestsInterface";

type AssetReviewRequestsOriginState = {
  __origin?: {
    currentPage?: number;
    status?: GetAssetReviewRequestsParams["status"];
    isBulkMode?: boolean;
    selectedIds?: number[];
    profilePath?: string;
  };
};

const statusToChip = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  switch (normalized) {
    case "pending":
      return { label: "Pending", bg: "#fff7e6", color: "#000" };
    case "awaiting_coach":
      return { label: "Awaiting Coach", bg: "#fff7e6", color: "#000" };
    case "in_progress":
      return { label: "In Progress", bg: "#e6f4ea", color: "#000" };
    case "completed":
      return { label: "Completed", bg: "#e6f4ea", color: "#000" };
    case "cancelled":
      return { label: "Cancelled", bg: "#fbeaea", color: "#000" };
    default:
      return { label: normalized || "-", bg: "#eee", color: "#000" };
  }
};

const Stars = ({ value = 0, max = 5 }: { value?: number; max?: number }) => {
  const filled = Math.max(0, Math.min(max, Number(value)));
  return (
    <span>
      {Array.from({ length: max }, (_, i) => (
        <span
          key={i}
          style={{
            color: i < filled ? "#f1c40f" : "#e0e0e0",
            fontSize: "18px",
            lineHeight: 1,
          }}
        >
          ★
        </span>
      ))}
    </span>
  );
};

// Removed user profile placeholder avatar per requirements

const Detail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialRow = location.state as AssetReviewRequest | undefined;
  const origin = (location.state as AssetReviewRequestsOriginState | undefined)
    ?.__origin;

  const [row, setRow] = useState<AssetReviewRequest | undefined>(initialRow);

  // minimal source of truth from row; API show payload if needed
  const asset = row?.asset;
  const gccId = asset?.gcc_id || asset?.kpoint_gcc_id;
  const hasGcc = typeof gccId === "string" && gccId.trim().length > 0;
  const videoUrl = hasGcc
    ? `${process.env.REACT_APP_KPOINT_API_HOST}web/videos/${gccId}`
    : undefined;
  const title = asset?.title || "-";
  const sportName = asset?.master_sport_name;
  const thumb = asset?.thumbnail_url;
  const requestorName = row?.requestor_name || "-";
  const coachName = row?.coach?.name || "-";
  const statusChip = statusToChip(row?.status);
  const isCompleted = (row?.status || "").toLowerCase() === "completed";
  const [paid, setPaid] = useState<boolean>(!!initialRow?.coach_paid_at);
  const isPending = (row?.status || "").toLowerCase() === "pending";
  const isAwaitingCoach =
    (row?.status || "").toLowerCase() === "awaiting_coach";
  const isInProgress = (row?.status || "").toLowerCase() === "in_progress";
  const currentCoachId = row?.coach?.id;

  const [reassignState, setReassignState] = useState<{
    show: boolean;
    masterSportId: number;
    currentCoachId?: number;
  }>({ show: false, masterSportId: 0 });

  const [highlights, setHighlights] = useState<
    Array<{
      id: number;
      highlight: string;
      start_milliseconds: number;
      end_milliseconds: number;
      kpoint_highlight_id: string;
      asset_id: number;
      asset_review_request_id: number | null;
      author: { id: number; name: string };
    }>
  >([]);
  const [highlightsTotal, setHighlightsTotal] = useState<number>(0);
  const [highlightsPage, setHighlightsPage] = useState<number>(1);
  const H_PER_PAGE = 20;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const resp = await getAssetReviewRequestById(Number(id));
        setRow(resp.data);
        setPaid(!!resp.data.coach_paid_at);
      } catch (e) {
        console.error("Failed to fetch review request details", e);
      }
    };
    fetchDetails();
  }, [id]);

  // Reset highlights when row/id changes
  useEffect(() => {
    setHighlights([]);
    setHighlightsPage(1);
  }, [row?.asset?.id, id]);

  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const assetId = row?.asset?.id;
        if (!assetId) return;
        const resp = await getKpointHighlights(
          Number(assetId),
          Number(id),
          highlightsPage,
          H_PER_PAGE
        );
        const list = resp.data || [];
        setHighlights(list);
        const total = resp.total_count || 0;
        setHighlightsTotal(total);
      } catch (e) {
        console.error("Failed to fetch highlights", e);
        setHighlights([]);
        setHighlightsTotal(0);
      }
    };
    fetchHighlights();
  }, [row, id, highlightsPage]);

  const reassignMutation = useMutation(
    ({
      reviewRequestId,
      coachId,
    }: {
      reviewRequestId: number;
      coachId: number;
    }) => reassignReviewRequest(reviewRequestId, coachId),
    {
      onSuccess: async () => {
        try {
          const resp = await getAssetReviewRequestById(Number(id));
          setRow(resp.data);
        } catch (e) {
          // silent
        }
        setReassignState({ show: false, masterSportId: 0 });
      },
      onError: (e) => {
        console.error("Failed to reassign coach", e);
      },
    }
  );

  const coachEmail = row?.coach_user_details?.email || "-";
  const coachContact = useMemo(() => {
    const code = row?.coach_user_details?.country_code;
    const num = row?.coach_user_details?.contact_number;
    if (!code && !num) return "-";
    if (code && num) return `${code} ${num}`;
    return code || num || "-";
  }, [row?.coach_user_details]);
  const requestorEmail = row?.requestor_user_details?.email || "-";
  const requestorContact = useMemo(() => {
    const code = row?.requestor_user_details?.country_code;
    const num = row?.requestor_user_details?.contact_number;
    if (!code && !num) return "-";
    if (code && num) return `${code} ${num}`;
    return code || num || "-";
  }, [row?.requestor_user_details]);

  const tatChip = (() => {
    if (!isPending) return null;
    const n = row?.days_remaining_for_tat;
    if (typeof n !== "number") return null;
    let label = "";
    let bg = "#e6f4ea"; // green-ish default
    if (n < 0) {
      label = "Overdue";
      bg = "#fbeaea"; // red-ish
    } else if (n === 0) {
      label = "Today";
      bg = "#fff7e6"; // yellow-ish
    } else {
      label = `${n} Days`;
      bg = "#e6f4ea"; // green-ish
    }
    return (
      <span
        style={{
          display: "inline-block",
          backgroundColor: bg,
          color: "#000",
          borderRadius: "9999px",
          padding: "2px 8px",
          fontSize: 12,
          lineHeight: 1.5,
          marginLeft: 8,
        }}
      >
        {label}
      </span>
    );
  })();

  const paymentChip = (() => {
    if (!isCompleted) return null;
    const label = paid ? "Paid" : "Unpaid";
    const bg = paid ? "#e6f4ea" : "#fff7e6"; // green-ish for paid, yellow-ish for unpaid
    return (
      <span
        style={{
          display: "inline-block",
          backgroundColor: bg,
          color: "#000",
          borderRadius: "9999px",
          padding: "2px 8px",
          fontSize: 12,
          lineHeight: 1.5,
          marginRight: 8,
        }}
      >
        {label}
      </span>
    );
  })();

  return (
    <div className="ss-page-content d-flex flex-column flex-1 scroll-y bg-white">
      <header className="ss-page-header d-flex align-items-center justify-content-between px-3">
        <div className="d-flex align-items-center justify-content-between flex-1">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faChevronLeft}
              onClick={() => {
                if (origin) {
                  // Prefer profile path if provided from the originating list under a user profile tab
                  if (origin.profilePath) {
                    navigate(origin.profilePath, {
                      state: { __origin: origin },
                    });
                    return;
                  }
                  // Check if we came from a coach-scoped or player-scoped context by looking at the current URL
                  const currentPath = location.pathname;
                  if (
                    currentPath.includes("/coaches/") &&
                    currentPath.includes("/coach-review-requests/")
                  ) {
                    // Extract coach ID from the current path
                    const coachIdMatch = currentPath.match(
                      /\/coaches\/(\d+)\/coach-review-requests\//
                    );
                    if (coachIdMatch) {
                      const coachId = coachIdMatch[1];
                      navigate(`/coaches/${coachId}/coach-review-requests`, {
                        state: { __origin: origin },
                      });
                    } else {
                      navigate("/review-requests", {
                        state: { __origin: origin },
                      });
                    }
                  } else if (
                    currentPath.includes("/players/") &&
                    currentPath.includes("/player-review-requests/")
                  ) {
                    // Extract player ID from the current path
                    const playerIdMatch = currentPath.match(
                      /\/players\/(\d+)\/player-review-requests\//
                    );
                    if (playerIdMatch) {
                      const playerId = playerIdMatch[1];
                      navigate(`/players/${playerId}/player-review-requests`, {
                        state: { __origin: origin },
                      });
                    } else {
                      navigate("/review-requests", {
                        state: { __origin: origin },
                      });
                    }
                  } else {
                    navigate("/review-requests", {
                      state: { __origin: origin },
                    });
                  }
                } else {
                  navigate(-1);
                }
              }}
              role="button"
            />
            <h2 className="fs-md mb-0 fw-medium px-2">{`Review Request #${
              id || "-"
            }`}</h2>
          </div>
        </div>
      </header>
      <hr className="ss-page-seprator mx-4 my-0" />
      <div className="ss-page-body d-flex flex-column flex-1 scroll-y py-4">
        <div className="px-4">
          <div
            className="d-grid gap-3"
            style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            {/* Asset Details */}
            <Card className="ss-card p-3 position-relative">
              <Card.Title>Asset Details</Card.Title>
              {isCompleted && (
                <div className="position-absolute top-0 end-0 p-2">
                  <Button
                    variant={paid ? "outline-danger" : "outline-success"}
                    size="sm"
                    onClick={async () => {
                      try {
                        await patchAssetReviewRequestPaidStatus(
                          Number(id),
                          !paid
                        );
                        setPaid(!paid);
                      } catch (e) {
                        console.error("Failed to update paid status", e);
                      }
                    }}
                  >
                    {paid ? "Mark Unpaid" : "Mark Paid"}
                  </Button>
                </div>
              )}
              <div className="d-flex align-items-start gap-3">
                <div className="thumbnail-box position-relative">
                  {thumb ? (
                    hasGcc && videoUrl ? (
                      <a
                        href={videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={thumb}
                          alt={`asset thumbnail ${title}`}
                          height="56px"
                        />
                      </a>
                    ) : (
                      <img
                        src={thumb}
                        alt={`asset thumbnail ${title}`}
                        height="56px"
                      />
                    )
                  ) : (
                    <div
                      style={{ width: 96, height: 56, background: "#eee" }}
                    />
                  )}
                </div>
                <div className="d-flex flex-column">
                  <div className="fw-bold" style={{ fontSize: 14 }}>
                    {sportName && (
                      <>
                        <span
                          className="text-muted"
                          style={{ fontSize: 12, fontWeight: "normal" }}
                        >
                          {sportName}
                        </span>
                        <span
                          className="text-muted"
                          style={{ fontSize: 12, fontWeight: "normal" }}
                        >
                          {" - "}
                        </span>
                      </>
                    )}
                    {hasGcc && videoUrl ? (
                      <a
                        href={videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </div>
                  <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: statusChip.bg,
                        color: statusChip.color,
                        borderRadius: "9999px",
                        padding: "1px 6px",
                        fontSize: 11,
                        lineHeight: 1.5,
                      }}
                    >
                      {statusChip.label}
                    </span>
                    {tatChip}
                    {isCompleted && paymentChip}
                  </div>
                  <div className="mt-1 text-muted" style={{ fontSize: 12 }}>
                    Review Rate:{" "}
                    {row?.review_rate_currency && row?.review_rate_value != null
                      ? `${row?.review_rate_currency} ${row?.review_rate_value}`
                      : "-"}
                  </div>
                  {isCompleted && (
                    <div className="mt-1 d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Rating:{" "}
                          {row?.requestor_rating_points == null ? (
                            "-"
                          ) : (
                            <Stars
                              value={Number(row?.requestor_rating_points) || 0}
                            />
                          )}
                        </div>
                      </div>
                      {row?.requestor_rating_comment && (
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Comment: {row.requestor_rating_comment}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Coach */}
            <Card className="ss-card p-2 position-relative">
              <Card.Title>Coach</Card.Title>
              {(isPending ||
                isAwaitingCoach ||
                (isInProgress && !currentCoachId)) && (
                <div className="position-absolute top-0 end-0 p-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      setReassignState({
                        show: true,
                        masterSportId: row?.asset?.master_sport_id || 0,
                        currentCoachId: row?.coach?.id,
                      })
                    }
                  >
                    Reassign
                  </Button>
                </div>
              )}
              <div className="d-flex align-items-start gap-2">
                <div>
                  <div className="fw-bold" style={{ fontSize: 14 }}>
                    {coachName}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Email: {coachEmail}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Contact: {coachContact}
                  </div>
                </div>
              </div>
            </Card>

            {/* Player Details */}
            <Card className="ss-card p-2">
              <Card.Title>Player Details</Card.Title>
              <div className="d-flex align-items-start gap-2">
                <div>
                  <div className="fw-bold" style={{ fontSize: 14 }}>
                    {requestorName}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Email: {requestorEmail}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Contact: {requestorContact}
                  </div>
                </div>
              </div>
            </Card>
          </div>
          {/* Highlights */}
          <Card className="ss-card p-3 mt-3">
            <Card.Title>
              Highlights{" "}
              <span className="text-muted" style={{ fontSize: 12 }}>
                ({highlightsTotal})
              </span>
            </Card.Title>
            <div className="d-flex flex-column gap-2">
              {highlights && highlights.length ? (
                <>
                  {highlights.map((h) => (
                    <div
                      key={h.id}
                      className="d-flex align-items-center justify-content-between p-2 border rounded"
                    >
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          {Math.round(h.start_milliseconds / 1000)}s -{" "}
                          {Math.round(h.end_milliseconds / 1000)}s
                        </span>
                        <span className="fw-medium">{h.highlight}</span>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-muted">No highlights found</div>
              )}
            </div>
            {highlightsTotal > H_PER_PAGE && (
              <div className="pagination-wrap d-flex justify-content-end mt-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={highlightsPage <= 1}
                  onClick={() => setHighlightsPage((p) => Math.max(1, p - 1))}
                  className="me-2"
                >
                  Prev
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={highlightsPage * H_PER_PAGE >= highlightsTotal}
                  onClick={() => setHighlightsPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
      <ReassignCoach
        show={reassignState.show}
        onClose={() => setReassignState({ show: false, masterSportId: 0 })}
        onReassign={(coachId: number) =>
          reassignMutation.mutate({ reviewRequestId: Number(id), coachId })
        }
        masterSportId={reassignState.masterSportId}
        currentCoachId={reassignState.currentCoachId}
      />
    </div>
  );
};

export default Detail;
